'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Plus, Pencil, Trash2, Check } from 'lucide-react';
import {
  CategoryType,
  CategoryEntry,
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  CATEGORY_DESCRIPTIONS,
  loadCategoryEntriesForCategory,
  addCategoryEntry,
  updateCategoryEntry,
  deleteCategoryEntry,
  getAllPreviousNamesFromDB,
} from '@/data/categoryData';


interface CategoryEntryFormProps {
  category: CategoryType;
  initialValues?: CategoryEntry;
  onSubmit: (entry: Omit<CategoryEntry, 'id' | 'eventId'>) => void;
  onCancel: () => void;
  saving: boolean;
  suggestions: string[];
}

function CategoryEntryForm({ category, initialValues, onSubmit, onCancel, saving, suggestions }: CategoryEntryFormProps) {
  const [name, setName] = useState(initialValues?.name || '');
  const [description, setDescription] = useState(initialValues?.description || '');
  const [location, setLocation] = useState(initialValues?.location || '');
  const [openTime, setOpenTime] = useState(initialValues?.openTime || '');
  const [closeTime, setCloseTime] = useState(initialValues?.closeTime || '');
  const [tags, setTags] = useState(initialValues?.tags || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (name.length >= 1) {
      const filtered = suggestions.filter(s =>
        s.toLowerCase().includes(name.toLowerCase()) && s !== name
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [name, suggestions]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), category, description, location, openTime, closeTime, tags });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Name with autocomplete */}
        <div className="sm:col-span-2 relative">
          <label className="block text-xs font-medium text-foreground mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            onFocus={() => name.length >= 1 && filteredSuggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            className="w-full px-3 py-2 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            placeholder={`z.B. ${CATEGORY_LABELS[category]}-Name`}
            required
          />
          {showSuggestions && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-muted border-b border-border">
                <span className="text-xs text-muted-foreground">Aus vorherigen Events:</span>
              </div>
              {filteredSuggestions.slice(0, 6).map(s => (
                <button
                  key={s}
                  type="button"
                  onMouseDown={() => { setName(s); setShowSuggestions(false); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Description */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-foreground mb-1">
            Beschreibung <span className="text-xs text-muted-foreground font-normal">(optional)</span>
          </label>
          <textarea
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all resize-none"
            placeholder="Kurze Beschreibung…"
          />
        </div>

        {/* Location */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">
            Standort / Platz <span className="text-xs text-muted-foreground font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={location}
            onChange={e => setLocation(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            placeholder="z.B. Marktplatz Nord"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">
            Tags <span className="text-xs text-muted-foreground font-normal">(optional, kommagetrennt)</span>
          </label>
          <input
            type="text"
            value={tags}
            onChange={e => setTags(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            placeholder="z.B. Musik, Tanz"
          />
        </div>

        {/* Open time */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">
            Öffnungszeit <span className="text-xs text-muted-foreground font-normal">(optional)</span>
          </label>
          <input
            type="time"
            value={openTime}
            onChange={e => setOpenTime(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all font-mono"
          />
        </div>

        {/* Close time */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">
            Schließzeit <span className="text-xs text-muted-foreground font-normal">(optional)</span>
          </label>
          <input
            type="time"
            value={closeTime}
            onChange={e => setCloseTime(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all font-mono"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="btn-primary flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? (
            <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <Check size={14} />
          )}
          {saving ? 'Speichern…' : initialValues ? 'Änderungen speichern' : 'Eintrag hinzufügen'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all"
        >
          <X size={14} />
          Abbrechen
        </button>
      </div>
    </form>
  );
}

interface CategoryViewProps {
  category: CategoryType;
  eventId: string;
  isAdmin?: boolean;
}

export function CategoryView({ category, eventId, isAdmin = false }: CategoryViewProps) {
  const [entries, setEntries] = useState<CategoryEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CategoryEntry | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(true);

  useEffect(() => {
    setLoadingEntries(true);
    Promise.all([
      loadCategoryEntriesForCategory(eventId, category),
      getAllPreviousNamesFromDB(category, eventId),
    ]).then(([loaded, prevNames]) => {
      setEntries(loaded);
      setSuggestions(prevNames);
      setLoadingEntries(false);
    });
  }, [eventId, category]);

  const handleAdd = async (data: Omit<CategoryEntry, 'id' | 'eventId'>) => {
    setSaving(true);
    const newEntry = await addCategoryEntry({ ...data, eventId });
    if (newEntry) {
      setEntries(prev => [...prev, newEntry]);
    }
    setShowAddForm(false);
    setSaving(false);
  };

  const handleEdit = async (data: Omit<CategoryEntry, 'id' | 'eventId'>) => {
    if (!editingEntry) return;
    setSaving(true);
    const updated = await updateCategoryEntry(editingEntry.id, { ...data, eventId });
    if (updated) {
      setEntries(prev => prev.map(e => e.id === editingEntry.id ? updated : e));
    }
    setEditingEntry(null);
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    const ok = await deleteCategoryEntry(id);
    if (ok) {
      setEntries(prev => prev.filter(e => e.id !== id));
    }
    setDeleteConfirm(null);
  };

  const filtered = searchQuery.trim().length >= 1
    ? entries.filter(e => {
        const q = searchQuery.toLowerCase();
        return (
          e.name.toLowerCase().includes(q) ||
          (e.description || '').toLowerCase().includes(q) ||
          (e.location || '').toLowerCase().includes(q) ||
          (e.tags || '').toLowerCase().includes(q)
        );
      })
    : entries;

  const label = CATEGORY_LABELS[category];
  const icon = CATEGORY_ICONS[category];
  const desc = CATEGORY_DESCRIPTIONS[category];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{icon}</span>
            <h2 className="font-display text-xl font-bold text-primary tracking-wide">{label}</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setShowAddForm(true); setEditingEntry(null); }}
            className="btn-primary flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Plus size={15} />
            Eintrag hinzufügen
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={`${label} durchsuchen…`}
          className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Add form */}
      {showAddForm && isAdmin && (
        <div className="card-elevated rounded-xl p-5 border-l-4 border-accent fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-sm font-semibold text-foreground">
              Neuen Eintrag hinzufügen — {label}
            </h3>
            <button onClick={() => setShowAddForm(false)} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>
          <CategoryEntryForm
            category={category}
            onSubmit={handleAdd}
            onCancel={() => setShowAddForm(false)}
            saving={saving}
            suggestions={suggestions}
          />
        </div>
      )}

      {/* Loading state */}
      {loadingEntries ? (
        <div className="space-y-2 animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-14 bg-muted rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-3">{icon}</p>
          <p className="font-display text-base font-semibold text-primary">
            {searchQuery ? 'Keine Ergebnisse' : `Noch keine ${label} eingetragen`}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {searchQuery
              ? `Kein Eintrag gefunden für „${searchQuery}"`
              : isAdmin
              ? `Klicke auf „Eintrag hinzufügen" um ${label} einzutragen.`
              : `Für dieses Event wurden noch keine ${label} eingetragen.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {searchQuery && (
            <p className="text-xs text-muted-foreground px-1">
              {filtered.length} Ergebnis{filtered.length !== 1 ? 'se' : ''} für „{searchQuery}"
            </p>
          )}
          {filtered.map(entry => (
            <div key={entry.id}>
              {editingEntry?.id === entry.id && isAdmin ? (
                <div className="card-elevated rounded-xl p-5 border-l-4 border-primary fade-in">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-sm font-semibold text-foreground">Eintrag bearbeiten</h3>
                    <button onClick={() => setEditingEntry(null)} className="text-muted-foreground hover:text-foreground">
                      <X size={16} />
                    </button>
                  </div>
                  <CategoryEntryForm
                    category={category}
                    initialValues={entry}
                    onSubmit={handleEdit}
                    onCancel={() => setEditingEntry(null)}
                    saving={saving}
                    suggestions={suggestions}
                  />
                </div>
              ) : (
                <div className="bg-card border border-border rounded-xl px-4 py-3 hover:border-primary/30 transition-colors group">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-foreground">{entry.name}</span>
                        {entry.openTime && (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full font-mono">
                            {entry.openTime}{entry.closeTime ? ` – ${entry.closeTime}` : ''} Uhr
                          </span>
                        )}
                      </div>
                      {entry.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{entry.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {entry.location && (
                          <span className="text-xs text-muted-foreground">📍 {entry.location}</span>
                        )}
                        {entry.tags && entry.tags.split(',').map(tag => (
                          <span key={tag.trim()} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                          onClick={() => { setEditingEntry(entry); setShowAddForm(false); }}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                          title="Bearbeiten"
                        >
                          <Pencil size={14} />
                        </button>
                        {deleteConfirm === entry.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="p-1.5 rounded-md text-white bg-red-500 hover:bg-red-600 transition-all"
                              title="Löschen bestätigen"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                              title="Abbrechen"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(entry.id)}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-all"
                            title="Löschen"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
